import { useCallback } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { useAuth } from '@web/walter-lib/auth'
import { useBreakpoints } from '@web/walter-ui-kit'
import { SettingsTabs } from './Menu/const'
import { QueryParams } from '~constants/routes'
import { generateQuery } from '~utils/generateQuery'
import { useIsomorphicLayoutEffect } from '~hooks/useIsomorphicLayoutEffect'

export const useSettingsModal = () => {
    const { isAuth } = useAuth()
    const { isMobile } = useBreakpoints()
    const navigate = useNavigate()
    const [searchParams, setSearchParams] = useSearchParams()
    const tab = searchParams.get(QueryParams.settings)?.toLowerCase() as SettingsTabs
    const type = searchParams.get(QueryParams.type) as 'crypto' | 'fiat' | undefined
    const fromCurrency = searchParams.get(QueryParams.fromCurrency) || ''
    const amount = searchParams.get(QueryParams.amount) || ''

    const defaultTab = isMobile ? SettingsTabs.menu : SettingsTabs.account

    const isOpen = searchParams.has(QueryParams.settings)
    const isValidTab = Boolean(tab) && tab in SettingsTabs

    const isDefaultTabRequired = isOpen && isAuth && !isValidTab
    const normalizedTab = isDefaultTabRequired ? defaultTab : tab

    const setTab = useCallback(
        (tab: SettingsTabs, state?: unknown) => {
            if (tab in SettingsTabs) {
                navigate(
                    { search: generateQuery({ settings: tab, type, fromCurrency, amount }) },
                    { state, replace: true }
                )
            }
        },
        [navigate, type, fromCurrency, amount]
    )

    const close = useCallback(() => {
        searchParams.delete(QueryParams.settings)
        searchParams.delete(QueryParams.sumsubLevel)
        setSearchParams(searchParams, { replace: true })
    }, [searchParams, setSearchParams])

    useIsomorphicLayoutEffect(() => {
        if (!isOpen) {
            return
        }

        if (!isAuth) {
            close()
        } else if (!isValidTab) {
            setTab(defaultTab)
        }
    }, [isOpen, isAuth, isValidTab, defaultTab, close, setTab])

    return {
        tab: normalizedTab,
        setTab,
        isOpen,
        close,
    }
}
